import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { maintenanceService } from '../../services/maintenance/planMaestroService';
import { toast } from 'react-hot-toast';
import {
    Warehouse,
    Plus,
    Search,
    MapPin,
    Phone,
    Mail,
    Edit,
    Trash2,
    Loader2,
    Star,
    Users
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import GestionMecanicos from './GestionMecanicos';

interface Taller {
    id: number;
    nombre: string;
    tipo: 'INTERNO' | 'EXTERNO';
    direccion: string;
    telefono: string;
    email: string;
    contacto_principal: string;
    calificacion_promedio: number;
    activo: boolean;
    mecanicos_count?: number;
}

export default function GestionTalleres() {
    const [talleres, setTalleres] = useState<Taller[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isMecanicosModalOpen, setIsMecanicosModalOpen] = useState(false);
    const [selectedTallerForMecanicos, setSelectedTallerForMecanicos] = useState<Taller | null>(null);
    const [editingTaller, setEditingTaller] = useState<Taller | null>(null);
    const [formData, setFormData] = useState<Partial<Taller>>({
        nombre: '',
        tipo: 'EXTERNO',
        direccion: '',
        telefono: '',
        email: '',
        contacto_principal: '',
        activo: true
    });

    const fetchTalleres = async () => {
        try {
            setLoading(true);
            const response = await maintenanceService.getTalleres();
            setTalleres(response.data);
        } catch (error) {
            console.error('Error fetching talleres:', error);
            toast.error('Error al cargar talleres');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTalleres();
    }, []);

    const handleOpenModal = (taller?: Taller) => {
        if (taller) {
            setEditingTaller(taller);
            setFormData(taller);
        } else {
            setEditingTaller(null);
            setFormData({
                nombre: '',
                tipo: 'EXTERNO',
                direccion: '',
                telefono: '',
                email: '',
                contacto_principal: '',
                activo: true
            });
        }
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        if (!formData.nombre?.trim()) {
            toast.error('El nombre es obligatorio');
            return;
        }

        const dataToSave = {
            ...formData,
            nombre: formData.nombre.trim(),
            email: formData.email?.trim() || null,
            telefono: formData.telefono?.trim() || null,
            direccion: formData.direccion?.trim() || null,
            contacto_principal: formData.contacto_principal?.trim() || null
        };

        try {
            if (editingTaller) {
                await maintenanceService.updateTaller(editingTaller.id, dataToSave);
                toast.success('Taller actualizado');
            } else {
                await maintenanceService.createTaller(dataToSave);
                toast.success('Taller creado');
            }
            setIsModalOpen(false);
            fetchTalleres();
        } catch (error: any) {
            console.error('Error saving taller:', error);
            const msg = error.response?.data?.message || 'Error al guardar taller';
            toast.error(msg);
        }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm('¿Estás seguro de eliminar este taller?')) {
            try {
                await maintenanceService.deleteTaller(id);
                toast.success('Taller eliminado');
                fetchTalleres();
            } catch (error) {
                toast.error('Error al eliminar');
            }
        }
    };

    const filteredTalleres = talleres.filter(t =>
        t.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.contacto_principal?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-4">
            <Card className="shadow-sm border-none bg-transparent">
                <CardHeader className="p-0 pb-4">
                    <div className="flex justify-between items-center">
                        <div>
                            <CardTitle className="text-2xl font-bold text-gray-800 tracking-tight">Directorio de Talleres</CardTitle>
                            <p className="text-sm text-gray-500 font-medium mt-1">Gestión de proveedores de servicio y talleres internos</p>
                        </div>
                        <Button className="bg-blue-600 hover:bg-blue-700 font-bold" onClick={() => handleOpenModal()}>
                            <Plus className="h-4 w-4 mr-2" /> Nuevo Taller
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="flex gap-4 mb-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <Input
                                placeholder="Buscar por nombre, contacto o email..."
                                className="pl-10 bg-white"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                        </div>
                    ) : filteredTalleres.length === 0 ? (
                        <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-200">
                            <Warehouse className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500 font-medium">No hay talleres registrados</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {filteredTalleres.map(taller => (
                                <Card key={taller.id} className="hover:shadow-md transition-all group">
                                    <CardContent className="p-5">
                                        <div className="flex justify-between items-start mb-3">
                                            <div className="flex items-center gap-3">
                                                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${taller.tipo === 'INTERNO' ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600'}`}>
                                                    <Warehouse className="h-5 w-5" />
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-gray-800">{taller.nombre}</h3>
                                                    <Badge variant="secondary" className="text-[10px] h-5">{taller.tipo}</Badge>
                                                </div>
                                            </div>
                                            <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Button variant="outline" size="sm" className="h-8 px-3 gap-1.5 text-blue-600 border-blue-200 hover:bg-blue-50" onClick={() => handleOpenModal(taller)}>
                                                    <Edit className="h-3.5 w-3.5" />
                                                    <span className="text-xs font-medium">Editar</span>
                                                </Button>
                                                <Button variant="outline" size="sm" className="h-8 px-3 gap-1.5 text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600" onClick={() => handleDelete(taller.id)}>
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                    <span className="text-xs font-medium">Eliminar</span>
                                                </Button>
                                            </div>
                                        </div>

                                        <div className="space-y-2 text-sm text-gray-600">
                                            {taller.direccion && (
                                                <div className="flex items-center gap-2">
                                                    <MapPin className="h-3.5 w-3.5 text-gray-400" />
                                                    <span className="truncate">{taller.direccion}</span>
                                                </div>
                                            )}
                                            {taller.telefono && (
                                                <div className="flex items-center gap-2">
                                                    <Phone className="h-3.5 w-3.5 text-gray-400" />
                                                    <span>{taller.telefono}</span>
                                                </div>
                                            )}
                                            {taller.email && (
                                                <div className="flex items-center gap-2">
                                                    <Mail className="h-3.5 w-3.5 text-gray-400" />
                                                    <span className="truncate">{taller.email}</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                                            <div className="flex items-center gap-1 text-xs font-medium text-gray-500">
                                                <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                                                {taller.calificacion_promedio || '0.0'}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-7 text-xs px-2"
                                                    onClick={() => {
                                                        setSelectedTallerForMecanicos(taller);
                                                        setIsMecanicosModalOpen(true);
                                                    }}
                                                >
                                                    <Users className="h-3 w-3 mr-1" />
                                                    {taller.mecanicos_count || 0} Personal
                                                </Button>
                                                <span className={`text-xs font-bold ${taller.activo ? 'text-green-600' : 'text-gray-400'}`}>
                                                    {taller.activo ? 'Activo' : 'Inactivo'}
                                                </span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editingTaller ? 'Editar Taller' : 'Nuevo Taller'}</DialogTitle>
                        <DialogDescription>Información del proveedor de servicio</DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">Nombre</label>
                            <Input
                                value={formData.nombre}
                                onChange={e => setFormData({ ...formData, nombre: e.target.value })}
                                placeholder="Ej. Mecánica Express"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="grid gap-2">
                                <label className="text-sm font-medium">Tipo</label>
                                <select
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    value={formData.tipo}
                                    onChange={e => setFormData({ ...formData, tipo: e.target.value as any })}
                                >
                                    <option value="EXTERNO">Externo</option>
                                    <option value="INTERNO">Interno</option>
                                </select>
                            </div>
                            <div className="grid gap-2">
                                <label className="text-sm font-medium">Teléfono</label>
                                <Input
                                    value={formData.telefono}
                                    onChange={e => setFormData({ ...formData, telefono: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">Dirección</label>
                            <Input
                                value={formData.direccion}
                                onChange={e => setFormData({ ...formData, direccion: e.target.value })}
                            />
                        </div>
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">Email</label>
                            <Input
                                value={formData.email}
                                type="email"
                                onChange={e => setFormData({ ...formData, email: e.target.value })}
                            />
                        </div>
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">Contacto Principal</label>
                            <Input
                                value={formData.contacto_principal}
                                onChange={e => setFormData({ ...formData, contacto_principal: e.target.value })}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsModalOpen(false)}>Cancelar</Button>
                        <Button onClick={handleSave}>Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {selectedTallerForMecanicos && (
                <GestionMecanicos
                    isOpen={isMecanicosModalOpen}
                    onClose={() => {
                        setIsMecanicosModalOpen(false);
                        fetchTalleres();
                    }}
                    tallerId={selectedTallerForMecanicos.id}
                    tallerNombre={selectedTallerForMecanicos.nombre}
                />
            )}
        </div>
    );
}
